/**
 * BuilderToolbox Component
 * 
 * Displays available block types that can be dragged onto the canvas.
 */

import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { BlockType, BLOCK_METADATA, getBlockMetadata } from '@/types/menuBuilder';
import { useMenuBuilderStore } from '@/stores/menuBuilderStore';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  LayoutTemplate,
  Grid3x3,
  Star,
  Image,
  Type,
  Minus,
  Space,
  Search,
  List,
} from 'lucide-react';

interface BuilderToolboxProps {
  className?: string;
}

// Map block types to their Lucide icons
const BLOCK_ICONS: Record<BlockType, React.ComponentType<{ className?: string }>> = {
  header: LayoutTemplate,
  category: Grid3x3,
  featured: Star,
  banner: Image,
  text: Type,
  divider: Minus,
  spacer: Space,
  search: Search,
  'category-navigation': List,
};

// Ordered list of block types for display
const BLOCK_TYPES: BlockType[] = [
  'header',
  'search',
  'category-navigation',
  'category',
  'featured',
  'banner',
  'text',
  'divider',
  'spacer',
];

export const BuilderToolbox = memo(function BuilderToolbox({
  className,
}: BuilderToolboxProps) {
  const { t } = useTranslation();
  const addBlock = useMenuBuilderStore((s) => s.addBlock);

  const handleAddBlock = (type: BlockType) => {
    addBlock(type);
  };

  return (
    <div className={cn('bg-background border-r flex flex-col', className)}>
      {/* Header */}
      <div className="p-4 border-b">
        <h3 className="font-semibold text-sm">
          {t('menuBuilder.blocks', 'Blocks')}
        </h3>
        <p className="text-xs text-muted-foreground mt-1">
          {t('menuBuilder.clickToAdd', 'Click to add to canvas')}
        </p>
      </div>

      {/* Block List */}
      <ScrollArea className="flex-1">
        <div className="p-2 space-y-1">
          {BLOCK_TYPES.map((type) => {
            const metadata = getBlockMetadata(type);
            if (!metadata) return null;
            const Icon = BLOCK_ICONS[type];

            return (
              <Tooltip key={type}>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    className="w-full justify-start gap-3 h-auto py-3"
                    onClick={() => handleAddBlock(type)}
                  >
                    <div className="w-8 h-8 rounded bg-muted flex items-center justify-center flex-shrink-0">
                      <Icon className="h-4 w-4" />
                    </div>
                    <div className="text-left">
                      <div className="font-medium text-sm">{metadata.name}</div>
                      <div className="text-xs text-muted-foreground">
                        {metadata.nameAr}
                      </div>
                    </div>
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="right">
                  {t(`menuBuilder.blockDesc.${type}`, getBlockDescription(type))}
                </TooltipContent>
              </Tooltip>
            );
          })}
        </div>
      </ScrollArea>
    </div>
  );
});

function getBlockDescription(type: BlockType): string {
  const descriptions: Record<BlockType, string> = {
    header: 'Restaurant branding - logo and name',
    search: 'Search bar for filtering items',
    'category-navigation': 'Sticky category tabs for quick navigation',
    category: 'Display a category with its items',
    featured: 'Highlight specific menu items',
    banner: 'Promotional banner or image',
    text: 'Custom text or announcements',
    divider: 'Visual separator between sections',
    spacer: 'Add vertical whitespace',
  };
  return descriptions[type];
}

export default BuilderToolbox;
